import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Factory } from "lucide-react";

const STAGES = ["RAW_MATERIAL", "G_PREPARATION", "G_LINE", "KILN", "POLISHING", "DESIGN", "QA", "PACKING"];
const STAGE_STATUSES = ["PENDING", "IN_PROGRESS", "COMPLETE", "BLOCKED", "NEEDS_ATTENTION"];

const STAGE_COLORS = {
  PENDING: "bg-slate-100 text-slate-700",
  IN_PROGRESS: "bg-amber-100 text-amber-900",
  COMPLETE: "bg-emerald-100 text-emerald-900",
  BLOCKED: "bg-red-100 text-red-900",
  NEEDS_ATTENTION: "bg-orange-100 text-orange-900",
};

export default function ProductionRuns() {
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(null); // production order detail
  const [stageForm, setStageForm] = useState({ stage: "G_LINE", status: "IN_PROGRESS", note: "" });

  const load = async () => {
    const { data } = await api.get("/manufacturing/production-runs");
    setOrders(data);
  };
  useEffect(() => { load(); }, []);

  const postStage = async () => {
    try {
      // Post narrative stage-update to history
      await api.post(`/manufacturing/production-orders/${open.id}/stage-update`, stageForm);
      // Also patch the machine-readable stage_status map so grid + kiln page reflect it
      await api.patch(`/manufacturing/production-orders/${open.id}`, {
        stage_status: { [stageForm.stage]: stageForm.status },
        current_stage: stageForm.stage,
      });
      toast.success("Stage update posted");
      const { data } = await api.get(`/manufacturing/production-orders/${open.id}`);
      setOpen(data);
      setStageForm({ stage: stageForm.stage, status: "IN_PROGRESS", note: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const patchStatus = async (o, status) => {
    try { await api.patch(`/manufacturing/production-orders/${o.id}`, { status }); toast.success(status); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="production-runs-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-3"><Factory className="w-7 h-7" /> Production runs</h1>
        <p className="text-muted-foreground">Per-order stage grid. Production sub-roles post complete / blocked / note updates.</p>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PO #</TableHead>
              <TableHead>Source SO</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Qty</TableHead>
              {STAGES.map((s) => <TableHead key={s} className="text-[10px] uppercase">{s.replace("_", " ")}</TableHead>)}
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((o) => {
              const ss = o.stage_status || {};
              return (
                <TableRow key={o.id} data-testid={`production-order-row-${o.id}`}>
                  <TableCell className="font-mono text-xs">{o.number}</TableCell>
                  <TableCell className="font-mono text-xs">{o.source_sales_order_number || "—"}</TableCell>
                  <TableCell className="text-sm">{o.product_name}</TableCell>
                  <TableCell className="font-mono">{o.quantity}</TableCell>
                  {STAGES.map((s) => {
                    const v = ss[s] || "PENDING";
                    return (
                      <TableCell key={s}>
                        <Badge variant="outline" className={`font-mono text-[10px] ${STAGE_COLORS[v] || ""}`}>{v.replace("_", " ")}</Badge>
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-right">
                    <div className="flex justify-end items-center gap-2">
                      <Select value={o.status || "QUEUED"} onValueChange={(v) => patchStatus(o, v)}>
                        <SelectTrigger className="h-7 w-28 text-[10px]" data-testid={`prod-order-status-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{["QUEUED", "IN_PRODUCTION", "READY", "COMPLETED", "BLOCKED", "CANCELLED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" onClick={() => setOpen(o)} data-testid={`production-order-open-${o.id}`}>Update</Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {orders.length === 0 && <TableRow><TableCell colSpan={STAGES.length + 5} className="text-center text-muted-foreground py-8">No production orders yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{open?.number} · {open?.product_name}</DialogTitle></DialogHeader>
          {open ? (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Quantity</div><div className="font-display font-black mt-1">{open.quantity}</div></div>
                <div className="rounded-lg border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Status</div><div className="font-display font-black mt-1">{open.status}</div></div>
                <div className="rounded-lg border border-border p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Source SO</div><div className="font-mono text-xs mt-1">{open.source_sales_order_number || "—"}</div></div>
              </div>

              <div>
                <h3 className="font-display font-bold mb-2">Stage status</h3>
                <div className="grid grid-cols-4 gap-2">
                  {STAGES.map((s) => {
                    const v = (open.stage_status || {})[s] || "PENDING";
                    return (
                      <div key={s} className="rounded-lg border border-border p-3">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.replace("_", " ")}</div>
                        <Badge variant="outline" className={`mt-1 font-mono text-[10px] ${STAGE_COLORS[v] || ""}`}>{v}</Badge>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="border border-border rounded-lg p-4 space-y-3">
                <h3 className="font-display font-bold">Post a stage update</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Stage</Label>
                    <Select value={stageForm.stage} onValueChange={(v) => setStageForm({ ...stageForm, stage: v })}>
                      <SelectTrigger data-testid="stage-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Status</Label>
                    <Select value={stageForm.status} onValueChange={(v) => setStageForm({ ...stageForm, status: v })}>
                      <SelectTrigger data-testid="stage-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>Note</Label><Input value={stageForm.note} onChange={(e) => setStageForm({ ...stageForm, note: e.target.value })} placeholder="Observation / blocker / next steps…" /></div>
                <Button onClick={postStage} data-testid="stage-post-btn">Post update</Button>
              </div>

              <div>
                <h3 className="font-display font-bold mb-2">Update log</h3>
                <div className="space-y-2">
                  {(open.stage_updates || []).slice().reverse().map((u) => (
                    <div key={u.id} className="text-xs border-l-2 border-orange-400 pl-3 py-1">
                      <div className="font-mono text-[10px] text-muted-foreground">{u.at ? new Date(u.at).toLocaleString() : ""}</div>
                      <div><b>{u.actor}</b> ({u.actor_role}) → <span className="font-mono">{u.stage}</span> · <Badge variant="outline" className={`font-mono text-[10px] ${STAGE_COLORS[u.status] || ""}`}>{u.status}</Badge></div>
                      {u.note ? <div className="italic text-muted-foreground mt-1">{u.note}</div> : null}
                    </div>
                  ))}
                  {(open.stage_updates || []).length === 0 ? <div className="text-xs text-muted-foreground">No updates yet.</div> : null}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
